import { Disclosure, Transition } from "@headlessui/react";
import clsx from "clsx";
import { ReactNode } from "react";
import { FCC } from "types";

import { ChevronDownSmall, ChevronLeft } from "@/assets/icons/ds";
import {
  ButtonContent,
  getBtnClassName,
} from "@/components/Interface/Button/Button";

type AccordionVariant = "default" | "list";

const variantClassMapping: Record<
  AccordionVariant,
  { container: { open: string; closed: string }; header: string }
> = {
  default: {
    container: {
      open: "rounded-md bg-surface-quaternary p-4",
      closed: "rounded-md bg-surface-quaternary p-4",
    },
    header: "items-center",
  },
  // The design system's dire-accordion-item inside a list: a divided row
  // while collapsed, a tinted card once expanded. The surrounding <li> needs
  // `group` so the last row drops its divider.
  list: {
    container: {
      open: "rounded-md bg-surface-quaternary p-4",
      closed: "border-b border-border-tertiary group-last:border-b-0",
    },
    header: "items-start py-4",
  },
};

const headingTag = { 2: "h2", 3: "h3", 4: "h4" } as const;

// The list variant's icon-only toggle: a small secondary DS button.
const toggleButtonStyle = {
  variant: "secondary",
  size: "sm",
  iconOnly: true,
} as const;

interface Props {
  className?: string;
  label?: string;
  /** Heading level of `label`. Accordions sit inside cards (h2), so h3 by default. */
  headingLevel?: 2 | 3 | 4;
  /** Replaces the `label` headline with custom content. */
  header?: ReactNode;
  /** Controls rendered next to the toggle, e.g. an edit button. */
  actions?: ReactNode;
  /** Accessible name of the icon-only toggle of the `list` variant. */
  toggleLabel?: string;
  canOpen?: boolean;
  /** Whether the panel starts expanded. Defaults to true (existing behaviour). */
  defaultOpen?: boolean;
  variant?: AccordionVariant;
}

export const Accordion: FCC<Props> = ({
  className,
  label,
  headingLevel = 3,
  header,
  actions,
  toggleLabel,
  canOpen = true,
  defaultOpen = true,
  variant = "default",
  children,
}) => {
  const variantClasses = variantClassMapping[variant];
  const Heading = headingTag[headingLevel];

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div
          className={clsx(
            "w-full",
            open
              ? variantClasses.container.open
              : variantClasses.container.closed,
            className,
          )}
        >
          <div
            data-testid="accordion-header"
            className={clsx(
              "flex w-full justify-between gap-4",
              variantClasses.header,
            )}
          >
            {header ?? (
              <Heading className="text-body-base font-semibold leading-md text-foreground-primary">
                {label}
              </Heading>
            )}
            {(actions || canOpen) && (
              <div className="flex shrink-0 items-center gap-2">
                {actions}
                {canOpen &&
                  (variant === "list" ? (
                    // Disclosure.Button has to own the <button>, so it borrows
                    // the DS button's classes and inside instead of nesting `Button`.
                    <Disclosure.Button
                      className={getBtnClassName(toggleButtonStyle)}
                      aria-label={toggleLabel}
                    >
                      <ButtonContent
                        {...toggleButtonStyle}
                        icon={
                          <ChevronDownSmall
                            data-testid="close-button"
                            className={clsx(open && "rotate-180")}
                          />
                        }
                      />
                    </Disclosure.Button>
                  ) : (
                    <Disclosure.Button
                      className="accordion-button shrink-0"
                      aria-label={label ?? toggleLabel}
                    >
                      <div
                        data-testid="close-button"
                        className={clsx(
                          "transform cursor-pointer bg-transparent transition ease-linear",
                          open ? "rotate-90" : "-rotate-90",
                        )}
                      >
                        <ChevronLeft className="size-6" />
                      </div>
                    </Disclosure.Button>
                  ))}
              </div>
            )}
          </div>
          <Transition
            as="div"
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Disclosure.Panel className="accordion-body">
              <div className="accordion-body mt-4">{children}</div>
            </Disclosure.Panel>
          </Transition>
        </div>
      )}
    </Disclosure>
  );
};
